import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import {
  LogistikCart,
  LogistikCartItem,
  LogistikChancenBriefCartItem,
  LogistikLearningMaterialCartItem,
  LogistikWorkshoppackCartiItem,
} from '@chancenwerk/db/LogistikCart.js';
import type { ChancenBriefVariableValue } from '@chancenwerk/db/LogistikOrder.js';
import {
  ChancenBrief,
  LearningMaterial,
  LogistikProductSet,
  TravelCertificate,
  WorkshopPack,
  type LogistikProduct,
} from '@chancenwerk/db/LogistikProduct.js';

type SelSet = {
  _id: 1;
  _class: 1;
  minOrderAmount: 1;
  maxOrderAmount: 1;
  availableQuantity: 1;
  productIDs: 1;
  trainersQuantity: 1;
  participantsQuantity: 1;
  isDigital: 1;
};
type NLProduct = NarrowedEntity<LogistikProduct, SelSet>;

export interface CartAddRequest {
  type: 'add';
  cart: LogistikCart;
  productsInCart: NLProduct[];
  product: NLProduct;
  quantity: number;
  tokenMaxOrderQuantity: Maybe<number>;
  isDigital: Maybe<boolean>;
  participantsQuantity: Maybe<number>;
  trainersQuantity: Maybe<number>;
  chancenBriefCartItem: Maybe<LogistikCartItem>;
}

export interface CartChangeRequest {
  type: 'change';
  cart: LogistikCart;
  newQuantity: number;
  product: NLProduct;
  productsInCart: Array<NLProduct>;
  cartItem: LogistikCartItem;
  tokenMaxOrderQuantity: Maybe<number>;
}

export type CartRequest = CartChangeRequest | CartAddRequest;

export interface ErrorResult {
  type: 'error';
  reason: string;
}
export interface AddResult {
  type: 'add';
  quantity: number;
  accepted: boolean;
  lower: boolean;
}
export interface ChangeResult {
  type: 'change';
  quantity: number;
  accepted: boolean;
  lower: boolean;
}
export interface NoneResult {
  type: 'none';
}
export interface RedundantResult {
  type: 'redundant';
}

export type AllowedChangeResult = ErrorResult | AddResult | ChangeResult | NoneResult | RedundantResult;

export function calculateAllowedCartChange(req: CartRequest): AllowedChangeResult {
  switch (req.type) {
    case 'add':
      return handleAddRequest(req);
    case 'change':
      return handleChangeRequest(req);
  }
}

function handleAddRequest(req: CartAddRequest): AllowedChangeResult {
  // - calculate total resulting quantity of the product
  // - find matching item in cart
  // - clamp that total to lower and upper bounds
  if (req.quantity < 0) return { type: 'none' };
  const productLowerBound = req.product.minOrderAmount;
  const productUpperBound = Math.min(req.product.maxOrderAmount, req.product.availableQuantity);
  if (productLowerBound > productUpperBound) return { type: 'none' };
  const matchingCartItem = req.cart.items.find(ci => isMatchingCartItem(ci, req));
  if (matchingCartItem) {
    if (isDigitalCartItem(matchingCartItem)) {
      // ? which return type is appropriate here?
      return { type: 'redundant' };
    }
    const changeResponse = handleChangeRequest({
      type: 'change',
      cart: req.cart,
      newQuantity: matchingCartItem.quantity + req.quantity,
      product: req.product,
      productsInCart: req.productsInCart,
      cartItem: matchingCartItem,
      tokenMaxOrderQuantity: req.tokenMaxOrderQuantity,
    });
    if (changeResponse.type === 'change')
      return {
        type: 'add',
        accepted: changeResponse.accepted,
        lower: changeResponse.lower,
        quantity: changeResponse.quantity - matchingCartItem.quantity,
      };
    else return changeResponse;
  } else if (req.isDigital) {
    req.quantity = 1;
  }
  // quantity of this product in total that fits in the cart
  const boundedTotalProductQuantity = Math.min(productUpperBound, Math.max(productLowerBound, req.quantity));
  // additional quantity of this product that fits in the cart - only bounded by product data, not by user data
  if (boundedTotalProductQuantity <= 0) return { type: 'none' };

  if (!req.tokenMaxOrderQuantity)
    return {
      type: 'add',
      quantity: boundedTotalProductQuantity,
      accepted: boundedTotalProductQuantity === req.quantity,
      lower: boundedTotalProductQuantity < req.quantity,
    };

  // further bound quantity by max order quantity - we need the set multiplier for this

  // multiplier to get from the quantity of this product to the total number of products that would be added to the cart
  // this is not always the same because we have sets, which contain multiple products
  const setMultiplier =
    req.product._class === LogistikProductSet._class ? (req.product as LogistikProductSet).productIDs.length : 1;
  if (setMultiplier === 0) return { type: 'error', reason: 'setMultiplier is 0' };
  // total number of products (unrolled sets) that are in the cart right now
  const currentCartTotalQuantity = req.cart.items
    .map(ci => {
      const product = req.productsInCart.find(p => p._id === ci.productID)!;
      if (product._class === LogistikProductSet._class)
        return (product as LogistikProductSet).productIDs.length * ci.quantity;
      else return ci.quantity;
    })
    .reduce((a, b) => a + b, 0);
  // number of products (unrolled sets) that would still fit in the cart
  const leftoverCartTotalQuantity = req.tokenMaxOrderQuantity - currentCartTotalQuantity;
  // quantity of this product (unrolled sets) that still fits in the cart
  const userBoundedAdditionalTotalQuantity = Math.min(
    boundedTotalProductQuantity * setMultiplier,
    leftoverCartTotalQuantity,
  );
  // quantity of this product that still fits in the cart - fully bounded by product and user data
  const fullyBoundedAdditionalProductQuantity = Math.floor(userBoundedAdditionalTotalQuantity / setMultiplier);
  if (fullyBoundedAdditionalProductQuantity === 0) return { type: 'none' };
  return {
    type: 'add',
    quantity: fullyBoundedAdditionalProductQuantity,
    accepted: fullyBoundedAdditionalProductQuantity === req.quantity,
    lower: fullyBoundedAdditionalProductQuantity < req.quantity,
  };
}

function handleChangeRequest(req: CartChangeRequest): AllowedChangeResult {
  // here, the user changed the quantity of an existing cart item
  // If the changed quantity refers to a digital cart item no change to the quantity should even be possible
  if (isDigitalCartItem(req.cartItem))
    return { type: 'error', reason: 'Changing the quantity of a digital cart item is not allowed' };
  const productLowerBound = req.product.minOrderAmount;
  const productUpperBound = Math.min(req.product.maxOrderAmount, req.product.availableQuantity);
  if (productLowerBound > productUpperBound) return { type: 'none' };
  if (productLowerBound > req.newQuantity)
    return { type: 'change', quantity: productLowerBound, accepted: false, lower: false };
  const quantity = Math.min(req.newQuantity, productUpperBound);
  if (!req.tokenMaxOrderQuantity)
    return { type: 'change', quantity, accepted: quantity === req.newQuantity, lower: quantity < req.newQuantity };
  // total quantity of products (unrolled sets) in the cart, disregarding the item we want to change
  const occupiedCartQuantity = req.cart.items
    .map(ci => {
      if (ci._id === req.cartItem._id) return 0;
      const product = req.productsInCart.find(p => p._id === ci.productID)!;
      if (product._class === LogistikProductSet._class)
        return (product as LogistikProductSet).productIDs.length * ci.quantity;
      else return ci.quantity;
    })
    .reduce((a, b) => a + b, 0);
  // number of products (unrolled sets) that would still fit in the cart
  const leftoverCartTotalQuantity = req.tokenMaxOrderQuantity - occupiedCartQuantity;
  // multiplier to get from the quantity of this product to the total number of products that would be added to the cart
  // this is not always the same because we have sets, which contain multiple products
  const setMultiplier =
    req.product._class === LogistikProductSet._class ? (req.product as LogistikProductSet).productIDs.length : 1;
  if (setMultiplier === 0) return { type: 'error', reason: 'setMultiplier is 0' };
  // quantity of this product (unrolled sets) that will fit in the cart
  const userBoundedTotalQuantity = Math.min(req.newQuantity * setMultiplier, leftoverCartTotalQuantity);
  // quantity of this product that will fit in the cart - fully bounded by product and user data
  const fullyBoundedAdditionalProductQuantity = Math.floor(userBoundedTotalQuantity / setMultiplier);
  return {
    type: 'change',
    quantity: fullyBoundedAdditionalProductQuantity,
    accepted: fullyBoundedAdditionalProductQuantity === req.newQuantity,
    lower: fullyBoundedAdditionalProductQuantity < req.newQuantity,
  };
}

function isDigitalCartItem(cartItem: LogistikCartItem): boolean {
  return (
    (cartItem._class === LogistikLearningMaterialCartItem._class &&
      (cartItem as LogistikLearningMaterialCartItem).isDigital) ||
    (cartItem._class === LogistikChancenBriefCartItem._class && (cartItem as LogistikChancenBriefCartItem).isSelfPrint)
  );
}

export function isMatchingCartItem(
  cartItem: LogistikCartItem,
  {
    product,
    trainersQuantity,
    participantsQuantity,
    isDigital,
    chancenBriefCartItem,
  }: {
    product: NLProduct;
    trainersQuantity: Maybe<number>;
    participantsQuantity: Maybe<number>;
    isDigital: Maybe<boolean>;
    chancenBriefCartItem: Maybe<LogistikCartItem>;
  },
): boolean {
  // Different product ids -> different cart items.
  if (cartItem.productID !== product._id) return false;
  // From here on out we know that the product is the same.
  // If product is learning material we check that isDigital matches.
  if (cartItem._class === LogistikLearningMaterialCartItem._class && product._class === LearningMaterial._class)
    return (cartItem as LogistikLearningMaterialCartItem).isDigital === isDigital;
  // If product is a workshop we check that trainers and participants quantities match.
  if (cartItem._class === LogistikWorkshoppackCartiItem._class && product._class === WorkshopPack._class)
    return (
      (cartItem as LogistikWorkshoppackCartiItem).trainersQuantity === trainersQuantity &&
      (cartItem as LogistikWorkshoppackCartiItem).participantsQuantity === participantsQuantity
    );
  // If product is a chancen brief we check that isSelfPrint matches and that chancen brief variables match.
  if (cartItem._class === LogistikChancenBriefCartItem._class && product._class === ChancenBrief._class)
    return (
      (cartItem as LogistikChancenBriefCartItem).isSelfPrint === isDigital &&
      areChancenBriefVariableArraysEqual(cartItem.flyaVariableValues, chancenBriefCartItem?.flyaVariableValues)
    );
  // If product is a travel certificate we check that chancen brief variables match exactly.
  if (cartItem._class == LogistikCartItem._class && product._class === TravelCertificate._class)
    return areChancenBriefVariableArraysEqual(cartItem.flyaVariableValues, chancenBriefCartItem?.flyaVariableValues);

  // we know ids match and that's all we need if we get to this line
  return true;
}

function areChancenBriefVariableArraysEqual(
  a: Maybe<ChancenBriefVariableValue[]>,
  b: Maybe<ChancenBriefVariableValue[]>,
): boolean {
  if (!isSome(a) && !isSome(b)) return true;
  if ((!isSome(a) && isSome(b)) || (isSome(a) && !isSome(b))) return false;
  if (!isSome(a) || !isSome(b)) {
    logger.error('a is none and b is none, which should never happen at this point');
    return false;
  }
  if (a.length !== b.length) return false;
  for (const avar of a) {
    if (!b.some(bvar => isChancenBriefVariableValueEqual(avar, bvar))) {
      return false;
    }
  }
  return true;
}

function isSome<T>(a: Maybe<T>): a is T {
  return a !== null && a !== undefined;
}

function isChancenBriefVariableValueEqual(a: ChancenBriefVariableValue, b: ChancenBriefVariableValue): boolean {
  return a.variableName === b.variableName && a.variableValue === b.variableValue;
}
